/**
 * Формати дат, часу, чисел і грошей через нативний Intl (stack.md, «i18n з першого дня»;
 * design-system/a11y-i18n.md, розд. 3; UX-42, UX-43, UX-44; PRF-03, PRF-04).
 *
 * - **Контекст** — мова й часовий пояс людини (`FormatContext`). Типово `uk` і Europe/Kyiv; чинні
 *   значення виставляє `region.ts` (пояс людини → пояс компанії → типовий). Зміна контексту
 *   повідомляється підписникам, а екрани перемальовує `useRegionKey()` у своїх коренях.
 * - **Кеш форматерів.** Конструктор Intl дорогий на слабкому ПК (PRF-03/04, бюджет перегляду): кожен
 *   форматер створюється один раз на пару «мова, пояс» і ключ-вид, далі береться з `Map`. Список
 *   видів — `DATE_OPTIONS` і `NUMBER_OPTIONS`; свого `new Intl.*` поза цим файлом не створюємо
 *   (тест `format.test.ts` рахує конструктори).
 * - Дані — в UTC, показ — у поясі людини. Поле «лише дата» поясу не має: `formatDateOnly` читає
 *   рік-місяць-день рядка і не зсувається при перегляді з іншого поясу.
 */
import type { PublicCompanyProfile } from '@clientline/contracts';
import { useSyncExternalStore } from 'react';

export const DEFAULT_TIME_ZONE = 'Europe/Kyiv';
/** Типовий пояс, коли жодного джерела немає. Лишається для старого коду й тестів. */
export const TIME_ZONE = DEFAULT_TIME_ZONE;
const DAY = 86_400_000;

/** Мови контракту (`SUPPORTED_LOCALES`): нова мова без запису в `INTL_TAG` не збереться. */
export type Locale = PublicCompanyProfile['locale'];

export interface FormatContext {
  locale: Locale;
  timeZone: string;
}

/** Мова → тег BCP 47 для Intl. Регіон `UA` дає розряди нерозривним пробілом і кому. */
const INTL_TAG: Record<Locale, string> = { uk: 'uk-UA' };

let context: FormatContext = { locale: 'uk', timeZone: DEFAULT_TIME_ZONE };
const listeners = new Set<() => void>();

export function getFormatContext(): FormatContext {
  return context;
}

/** Підписка для `useSyncExternalStore`. */
export function subscribeFormatContext(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Підписка екрана на зміну мови чи поясу: компонент перемальовується, і його дочірні — теж (крім
 * `memo`: тому в тих, що форматують дати, ключ іде пропом). Викликають кореневі компоненти екранів
 * (список, картка, профіль, адмінка, панель «Сьогодні»), а не кожен компонент з датою. Екран, що
 * форматує дати й не стоїть під таким коренем, мусить викликати його сам. Перемонтування за ключем
 * тут не вживаємо: воно губить фокус, прокрутку й незбережене введення (рев'ю #351).
 */
export function useRegionKey(): string {
  const ctx = useSyncExternalStore(subscribeFormatContext, getFormatContext, getFormatContext);
  return `${ctx.locale}|${ctx.timeZone}`;
}

const validZones = new Map<string, boolean>();

/** Чи знає цей рушій назву поясу (`Intl` кидає `RangeError` на невідомій). */
export function isKnownZone(zone: string): boolean {
  const known = validZones.get(zone);
  if (known !== undefined) return known;
  let ok = true;
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone });
  } catch {
    ok = false;
  }
  validZones.set(zone, ok);
  return ok;
}

/**
 * Змінити мову чи пояс. Невідомий рушію пояс ігноруємо: форматери не мають кидати на екрані.
 * Повертає `true`, якщо контекст справді змінився.
 */
export function setFormatContext(next: Partial<FormatContext>): boolean {
  const locale = next.locale ?? context.locale;
  const timeZone =
    next.timeZone !== undefined && isKnownZone(next.timeZone) ? next.timeZone : context.timeZone;
  if (locale === context.locale && timeZone === context.timeZone) return false;
  context = { locale, timeZone };
  for (const l of listeners) l();
  return true;
}

// ------------------------------------------------------------------ кеш форматерів

const DATE_OPTIONS = {
  date: { day: '2-digit', month: '2-digit', year: 'numeric' },
  time: { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
  dateTime: {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  },
  dayMonth: { day: '2-digit', month: '2-digit' },
  weekday: { weekday: 'short' },
  longDay: { day: 'numeric', month: 'long' },
  fullDay: { weekday: 'long', day: 'numeric', month: 'long' },
  /** Тільки для ключа календарного дня: `en-CA` дає ISO `2026-10-06`. */
  dayKey: {},
  /** Стінний час для `<input type="datetime-local">`: `2026-10-06`, `09:30` (частини збирає `toZonedInput`). */
  wall: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  },
  offset: { timeZoneName: 'longOffset' },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

const NUMBER_OPTIONS = {
  integer: { maximumFractionDigits: 0 },
  cents: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  decimal: { maximumFractionDigits: 20 },
  compact: { notation: 'compact', maximumFractionDigits: 1 },
} as const satisfies Record<string, Intl.NumberFormatOptions>;

type DateKind = keyof typeof DATE_OPTIONS;
type NumberKind = keyof typeof NUMBER_OPTIONS;

const dateCache = new Map<string, Intl.DateTimeFormat>();
const numberCache = new Map<string, Intl.NumberFormat>();
const pluralCache = new Map<string, Intl.PluralRules>();

function dateFormat(kind: DateKind, zone = context.timeZone): Intl.DateTimeFormat {
  const key = `${context.locale}|${zone}|${kind}`;
  let fmt = dateCache.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(kind === 'dayKey' ? 'en-CA' : INTL_TAG[context.locale], {
      ...DATE_OPTIONS[kind],
      timeZone: zone,
    });
    dateCache.set(key, fmt);
  }
  return fmt;
}

function numberFormat(kind: NumberKind): Intl.NumberFormat {
  const key = `${context.locale}|${kind}`;
  let fmt = numberCache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(INTL_TAG[context.locale], NUMBER_OPTIONS[kind]);
    numberCache.set(key, fmt);
  }
  return fmt;
}

function pluralRules(): Intl.PluralRules {
  let rules = pluralCache.get(context.locale);
  if (!rules) {
    rules = new Intl.PluralRules(INTL_TAG[context.locale]);
    pluralCache.set(context.locale, rules);
  }
  return rules;
}

/** Скільки форматерів створено — для тесту й діагностики. */
export function formatCacheSize(): number {
  return dateCache.size + numberCache.size + pluralCache.size;
}

// ------------------------------------------------------------------ числа й гроші (UX-44)

/** Число рядком від сервера: `numeric` приходить текстом без втрати точності. */
const DECIMAL_TEXT = /^-?\d+(\.\d+)?$/;
const asNumeric = (value: number | string): number | string =>
  typeof value === 'string' && DECIMAL_TEXT.test(value) ? value : Number(value);

/** `1 234,5`: розряди нерозривним пробілом, десяткова кома, без зайвих нулів. */
export function formatNumber(value: number | string): string {
  return numberFormat('decimal').format(asNumeric(value) as number);
}

/** Коротко для лічильників: `1,2 тис.`. */
export function formatCompact(value: number): string {
  return numberFormat('compact').format(value);
}

/** `12,5 %` з нерозривним пробілом перед знаком (a11y-i18n.md, розд. 3). */
export function formatPercent(value: number | string): string {
  return `${formatNumber(value)} %`;
}

/** Дробових одиниць валюти — з `Intl` (ISO 4217): довідника валют у вебі не тримаємо. */
const digitsCache = new Map<string, number>();
export function currencyDigits(code: string): number {
  let digits = digitsCache.get(code);
  if (digits === undefined) {
    try {
      digits =
        new Intl.NumberFormat('en', { style: 'currency', currency: code }).resolvedOptions()
          .maximumFractionDigits ?? 2;
    } catch {
      digits = 2;
    }
    digitsCache.set(code, digits);
  }
  return digits;
}

const moneyCache = new Map<string, Intl.NumberFormat>();
function moneyNumberFormat(digits: number): Intl.NumberFormat {
  const key = `${context.locale}|${digits}`;
  let fmt = moneyCache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(INTL_TAG[context.locale], {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
    moneyCache.set(key, fmt);
  }
  return fmt;
}

/**
 * `245 000 ₴`, з копійками — `245 000,50 ₴`; інша валюта — код ISO після числа: `1 200 USD`
 * (design-system/a11y-i18n.md, розд. 3; components.md, «Поле «гроші»»). Сума з API — десятковий
 * рядок: форматуємо його без float, тож копійки великих сум не губляться. Ціле — без `,00`, з
 * дробом — усі знаки валюти. Між числом і валютою — нерозривний пробіл.
 */
export function formatMoney(value: number | string, currency = 'UAH'): string {
  const numeric = asNumeric(value);
  const fraction = /\.(\d+)$/.exec(String(numeric))?.[1]?.replace(/0+$/, '') ?? '';
  const digits = fraction ? Math.max(currencyDigits(currency), fraction.length) : 0;
  const n = moneyNumberFormat(digits).format(numeric as number);
  return `${n}\u00a0${currency === 'UAH' ? '₴' : currency}`;
}

/**
 * Введене число: `250000`, `250 000`, `250000,50`, `250000.50`, `-1 200` → число. Порожньо — `null`,
 * не число — `NaN` (UX-44). Розділювач розрядів — будь-який пробіл (звичайний, нерозривний, вузький).
 */
export function parseNumberInput(text: string): number | null {
  const compact = text.replace(/[\s  ]/g, '').replace(',', '.');
  if (compact === '') return null;
  return /^[-+]?(\d+\.?\d*|\.\d+)$/.test(compact) ? Number(compact) : Number.NaN;
}

// ------------------------------------------------------------------ дати й час (UX-43)

/** Дата без часу: `06.10.2026` (a11y-i18n.md, розд. 3; `dateStyle: 'short'` дає `06.10.26`). */
export function formatDate(iso: string): string {
  return dateFormat('date').format(Date.parse(iso));
}

export function formatTime(iso: string): string {
  return dateFormat('time').format(Date.parse(iso));
}

/** `06.10.2026, 09:30` у поясі людини. */
export function formatDateTime(iso: string | number | Date): string {
  const ms = typeof iso === 'string' ? Date.parse(iso) : +iso;
  return dateFormat('dateTime').format(ms);
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Поле «лише дата» (`2026-10-06`): поясу немає, тож день не зсувається для людей у різних поясах
 * (UX-43). Рядок читається як календарний день в UTC і форматується в UTC.
 */
export function formatDateOnly(value: string): string {
  const m = DATE_ONLY.exec(value);
  if (!m) return value;
  return dateFormat('date', 'UTC').format(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

/** `+380670001234` → `+380 67 000 12 34`. Інші формати показуємо як є. */
export function formatPhone(e164: string): string {
  const m = /^\+380(\d{2})(\d{3})(\d{2})(\d{2})$/.exec(e164);
  return m ? `+380 ${m[1]} ${m[2]} ${m[3]} ${m[4]}` : e164;
}

/** Календарний день у поясі людини: `2026-10-06`. */
export function dayKey(ms: number): string {
  return dateFormat('dayKey').format(ms);
}

/** Різниця в календарних днях (від сьогодні): 0 — сьогодні, −1 — учора. */
export function dayDiff(iso: string, now: number): number {
  const a = Date.parse(`${dayKey(Date.parse(iso))}T00:00:00Z`);
  const b = Date.parse(`${dayKey(now)}T00:00:00Z`);
  return Math.round((a - b) / DAY);
}

// ------------------------------------------------------------------ стінний час у поясі людини

/**
 * Момент → значення `<input type="datetime-local">` (`2026-10-06T09:30`) у поясі людини, а не
 * браузера (UX-43): полем редагують той самий час, що й бачать у списку. Невалідний момент — `''`.
 * `zone` — інший пояс (пояс відповіді сервера, «Сьогодні»); невідомий — пояс людини.
 */
export function toZonedInput(iso: string | number, zone = context.timeZone): string {
  const ms = typeof iso === 'string' ? Date.parse(iso) : iso;
  if (Number.isNaN(ms)) return '';
  const p: Record<string, string> = {};
  const wall = dateFormat('wall', isKnownZone(zone) ? zone : context.timeZone);
  for (const part of wall.formatToParts(ms)) p[part.type] = part.value;
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

const WALL = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * Значення `datetime-local` (стінний час у поясі людини) → момент у мс. Переведення годинників:
 * неіснуючий час (весняний «пропуск») зсувається вперед, повторний (осінній) бере перше входження.
 * Невалідний рядок — `NaN`.
 */
export function fromZonedInput(local: string, zone = context.timeZone): number {
  const m = WALL.exec(local);
  if (!m) return Number.NaN;
  const [year, month, day, hour, minute, second] = m.slice(1).map((part) => Number(part ?? 0)) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  const wall = Date.UTC(year, month - 1, day, hour, minute, second);
  // Зсув за добу до і за добу після: різні — поруч переведення годинників.
  const before = zoneOffsetMinutes(zone, wall - DAY);
  const after = zoneOffsetMinutes(zone, wall + DAY);
  const offsets = before === after ? [before] : [before, after];
  const valid = offsets
    .map((offset) => wall - offset * 60_000)
    .filter((ms, i) => zoneOffsetMinutes(zone, ms) === offsets[i]);
  // Один зсув — звичайний час; два — повторна година (беремо першу); жодного — «пропущена» година.
  return valid.length > 0 ? Math.min(...valid) : wall - before * 60_000;
}

/** Початок календарного дня (`2026-10-06`) у поясі людини, мс. */
export function zonedDayStart(day: string): number {
  return fromZonedInput(`${day}T00:00`);
}

/** Календарний день зі зсувом у днях: `2026-10-06` + 1 → `2026-10-07` (без годин, тож без пасток DST). */
export function shiftDay(day: string, days: number): string {
  const ms = Date.parse(`${day}T00:00:00Z`) + days * DAY;
  return new Date(ms).toISOString().slice(0, 10);
}

/** День тижня в поясі людини: 0 — неділя … 6 — субота. */
export function weekdayOf(ms: number): number {
  return new Date(`${dayKey(ms)}T00:00:00Z`).getUTCDay();
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Дата кроку для списку: «Сьогодні, 10:00», «Завтра, 10:00», «Учора, 16:00», «Пн, 11:00», «08.10, 09:00». */
export function formatStepDate(iso: string, now: number): string {
  const diff = dayDiff(iso, now);
  const t = formatTime(iso);
  if (diff === 0) return `Сьогодні, ${t}`;
  if (diff === 1) return `Завтра, ${t}`;
  if (diff === -1) return `Учора, ${t}`;
  if (diff < 0 && diff > -7)
    return `${capitalize(dateFormat('weekday').format(Date.parse(iso)))}, ${t}`;
  return `${dateFormat('dayMonth').format(Date.parse(iso))}, ${t}`;
}

/** Підпис дня в лінії клієнта: «Сьогодні», «Учора», «2 жовтня». */
export function formatDayLabel(iso: string, now: number): string {
  const diff = dayDiff(iso, now);
  if (diff === 0) return 'Сьогодні';
  if (diff === -1) return 'Учора';
  return dateFormat('longDay').format(Date.parse(iso));
}

/** «вівторок, 6 жовтня» — заголовок панелі «Сьогодні». */
export function formatFullDay(ms: number): string {
  return dateFormat('fullDay').format(ms);
}

/** Коротка мітка для прострочених задач: «Учора», «Пн». */
export function formatOverdueLabel(iso: string, now: number): string {
  const diff = dayDiff(iso, now);
  if (diff === -1) return 'Учора';
  if (diff < 0 && diff > -7) return capitalize(dateFormat('weekday').format(Date.parse(iso)));
  return dateFormat('dayMonth').format(Date.parse(iso));
}

// ------------------------------------------------------------------ два пояси (UX-43, PRF-04)

/** Час `HH:mm` у заданому поясі (не в поясі людини): «зараз тут 14:32». */
export function formatTimeInZone(ms: number, zone: string): string {
  return dateFormat('time', isKnownZone(zone) ? zone : context.timeZone).format(ms);
}

/** Зсув пояса від UTC у хвилинах у цей момент (`GMT+03:00` → 180). */
export function zoneOffsetMinutes(zone: string, ms: number): number {
  const part = dateFormat('offset', zone)
    .formatToParts(ms)
    .find((p) => p.type === 'timeZoneName')?.value;
  const m = part ? /([+-])(\d{1,2})(?::?(\d{2}))?$/.exec(part) : null;
  if (!m) return 0;
  const minutes = Number(m[2]) * 60 + Number(m[3] ?? 0);
  return m[1] === '-' ? -minutes : minutes;
}

/**
 * Час спільного об'єкта (задача колеги, зустріч) з підказкою другого поясу, якщо він інший, ніж
 * у людини: людина у Варшаві бачить «10:00 (Київ — 11:00)» для задачі на 11:00 за Києвом.
 * Однаковий зсув у цей момент — просто час. `label` — назва місця другого поясу (`zoneLabel`).
 */
export function formatTimeWithZone(iso: string, otherZone: string, label: string): string {
  const ms = Date.parse(iso);
  const mine = formatTime(iso);
  if (
    !isKnownZone(otherZone) ||
    zoneOffsetMinutes(otherZone, ms) === zoneOffsetMinutes(context.timeZone, ms)
  ) {
    return mine;
  }
  return `${mine} (${label} — ${dateFormat('time', otherZone).format(ms)})`;
}

// ------------------------------------------------------------------ множини

/** ICU-множини для української: one / few / many (`1 задача`, `3 задачі`, `5 задач`). */
export function plural(n: number, forms: { one: string; few: string; many: string }): string {
  const rule = pluralRules().select(n);
  const word = rule === 'one' ? forms.one : rule === 'few' ? forms.few : forms.many;
  return `${n} ${word}`;
}

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

/**
 * Тривалість коротко: «< 1 год», «5 год», «3 дн.» (календарні доби, як «днів на етапі», PIP-17).
 * Спільна для історії переходів і рядка етапу в лінії клієнта (чанки картки й стрічки окремі).
 */
export function shortDuration(ms: number): string {
  if (ms < HOUR_MS) return '< 1 год';
  if (ms < DAY_MS) return `${Math.floor(ms / HOUR_MS)} год`;
  return `${Math.floor(ms / DAY_MS)} дн.`;
}
